import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Task } from '@/api';
import { useWebsocket } from '@/common/providers/websocket-provider';
import { addOrUpdateAllTasksCache } from './tasks-cache';

/**
 * Keeps `useFetchTasks` fresh: subscribes to the company's tasks and writes
 * every pushed task into the cached lists.
 */
export function useEnableTasksSockets(companyId: string) {
    const { subscribe, unsubscribe, on, off } = useWebsocket();
    useEffect(() => {
        const ids = subscribe([{ resource: `companies#${companyId}.tasks` }]);
        return () => {
            unsubscribe(ids);
        };
    }, [companyId, subscribe, unsubscribe]);

    const queryClient = useQueryClient();

    useEffect(() => {
        const tasksCallback = (message: unknown) => {
            const { data } = message as { data: { task: Task } };
            // All, because if we're on the tasks page and click a task, we expect it to instantly show in the mini task view as the fetchTask cache has task data
            addOrUpdateAllTasksCache(queryClient, data.task);
        };
        on('tasks_v3', tasksCallback);

        return () => {
            off('tasks_v3', tasksCallback);
        };
    }, [on, off, queryClient]);
}
